import { Button, ButtonProps, Icon, Skeleton, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { GET_FORM_TEMPLATE_FORMS_COUNT } from "src/scenes/orgAdmin/formTemplates/graphql/queries";
import { evictFormTemplateCache } from "src/services/apolloCacheManager";
import * as Url from "src/services/url";
import { ENROLLMENT_PERIOD_TAB_HASH } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { GenericError } from "../Feedback/GenericError";
import { DELETE_FORM_TEMPLATE } from "../graphql/mutations";
import { ConditionalTooltip } from "../Layout/ConditionalTooltip";
import { RemoteDataView } from "../Layout/RemoteDataView";
import { useNavigationBlockerContext } from "../NavigationBlocker/context";
import { Glossary } from "../Text/Glossary";

interface DeleteFormTemplateButtonProps {
  id: string;
  formTemplateName: string;
  buttonProps?: ButtonProps;
}

export const DeleteFormTemplateButton = ({
  id,
  formTemplateName,
  buttonProps,
}: DeleteFormTemplateButtonProps) => {
  const { glossary } = useGlossary();
  const navigate = useNavigate();
  const organization = useOrganization();
  const { enrollmentPeriodId = "" } = useParams();
  const blocker = useNavigationBlockerContext();
  const permissionsRD = RD.fromUserPermissions<Error>(useUserPermissions());

  const toast = useAvelaToast();

  const [deleteFormTemplate] = useRemoteDataMutation<
    GQL.DeleteFormTemplate,
    GQL.DeleteFormTemplateVariables
  >(DELETE_FORM_TEMPLATE);

  const { remoteData: formsCountRD } = useRemoteDataQuery<
    GQL.GetFormTemplateFormsCount,
    GQL.GetFormTemplateFormsCountVariables
  >(GET_FORM_TEMPLATE_FORMS_COUNT, {
    variables: { form_template_id: id },
    fetchPolicy: "network-only",
  });

  const handleDeleteFormTemplate = useCallback(async () => {
    try {
      await deleteFormTemplate({
        variables: { id: id },
        update: evictFormTemplateCache,
      });

      if (!toast.isActive(id)) {
        toast({
          id,
          title: glossary`Form deleted`,
          isClosable: true,
        });
      }

      navigate(
        organization
          .map((org) =>
            Url.OrgAdmin.Enrollments.edit(
              org,
              enrollmentPeriodId,
              ENROLLMENT_PERIOD_TAB_HASH.FORMS
            )
          )
          .withDefault("#")
      );
    } catch (error) {
      console.error(error);
      if (!toast.isActive(id)) {
        toast({
          id,
          title: glossary`Error deleting form`,
          description:
            "Please try again later or report the problem to our support team.",
          isClosable: true,
          status: "error",
        });
      }
    }
  }, [
    deleteFormTemplate,
    id,
    toast,
    navigate,
    organization,
    glossary,
    enrollmentPeriodId,
  ]);

  const { confirm, confirmationDialog: deleteConfirmationDialog } =
    useConfirmationDialog({
      header: glossary`Delete ${formTemplateName}?`,
      body: (
        <Text>
          <Glossary>
            Deleting this form will remove it from the organization, it will no
            longer be visible to any user. This action can’t be undone nor
            recovered.
          </Glossary>
        </Text>
      ),
      cancelButton: {
        label: "Cancel",
        colorScheme: "primary",
      },
      confirmButton: {
        label: "Yes, delete",
      },
      translate: true,
    });

  const handleDeleteClick = useCallback(async () => {
    blocker.disable(true);

    if (!(await confirm())) {
      blocker.disable(false);
      return;
    }

    handleDeleteFormTemplate();
  }, [blocker, confirm, handleDeleteFormTemplate]);

  const loading =
    buttonProps?.size === "sm" ? (
      <Skeleton height="2rem" width="6rem" />
    ) : (
      <Skeleton height="2.5rem" width="8rem" />
    );

  return (
    <RemoteDataView
      remoteData={RD.toTuple(formsCountRD, permissionsRD)}
      loading={loading}
      error={(error) => <GenericError error={error} variant="inline" />}
    >
      {([formsCount, permissions]) => {
        if (!permissions.hasOne("form_template:delete")) {
          return null;
        }

        const count =
          formsCount.form_template_by_pk?.forms_aggregate.aggregate?.count ?? 0;
        const isDisabled = count > 0;
        return (
          <>
            <ConditionalTooltip
              showTooltip={isDisabled}
              label={
                <Text align="center">
                  This form can be deleted once it has no form responses in it
                </Text>
              }
              hasArrow
              placement="left"
              borderRadius="md"
              p={2}
            >
              <Button
                aria-label="Delete form"
                leftIcon={<Icon as={RiDeleteBin6Line} />}
                variant="outline"
                onClick={handleDeleteClick}
                isDisabled={isDisabled}
                {...buttonProps}
              >
                Delete
              </Button>
            </ConditionalTooltip>

            {deleteConfirmationDialog}
          </>
        );
      }}
    </RemoteDataView>
  );
};
